// #region Imports
import { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
  DEFAULT_ALLOWED_MIME_TYPES,
  sniffMimeType,
  validateAttachments,
  type AttachmentConstraints,
  type AttachmentValidationError,
} from '../../utils/fileUtils';
// #endregion

// #region Types
const ATTACHMENTS_FIELD = 'attachments';

interface UseAttachmentsReturn {
  files: File[];
  errors: AttachmentValidationError[];
  addFiles: (incoming: FileList | File[]) => Promise<void>;
  removeFile: (index: number) => void;
}
// #endregion

// #region Internals

/**
 * Reads the first `n` bytes of a `File` into a `Uint8Array` using `FileReader`
 * (universally supported, including jsdom). Avoids `Blob.arrayBuffer()` which is
 * not available in all jsdom versions.
 */
const readHeaderBytes = (file: File, n = 12): Promise<Uint8Array> =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.onload = () => {
      const buf = reader.result;
      resolve(buf instanceof ArrayBuffer ? new Uint8Array(buf).slice(0, n) : new Uint8Array(0));
    };
    reader.onerror = () => resolve(new Uint8Array(0));
    reader.readAsArrayBuffer(file.slice(0, n));
  });

// #endregion

// #region Hook
/**
 * Manages bug-report attachment selection.
 * Holds the raw `File[]`, validates them synchronously for instant UX feedback,
 * sniffs each new file's magic bytes to detect disguised content (e.g. an .exe
 * renamed .png), and mirrors the list into the react-hook-form `attachments` field
 * so it flows into the submit payload (where it is encoded to base64 before dispatch).
 */
const useAttachments = (constraints?: AttachmentConstraints): UseAttachmentsReturn => {
  const { register, setValue, unregister } = useFormContext();
  const [files, setFiles] = useState<File[]>([]);
  const [errors, setErrors] = useState<AttachmentValidationError[]>([]);

  // Keep a ref to the current files so async closures read the latest value
  // without stale captures (anti-race for rapid consecutive selections).
  const filesRef = useRef<File[]>(files);
  filesRef.current = files;

  // Register the field once so its value is included in the submitted form data.
  useEffect(() => {
    register(ATTACHMENTS_FIELD);
    return () => unregister(ATTACHMENTS_FIELD);
  }, [register, unregister]);

  const commit = useCallback(
    (next: File[], extraErrors: AttachmentValidationError[] = []) => {
      setFiles(next);
      const syncErrors = validateAttachments(next, constraints);
      // Merge content errors with sync errors in one setState call to avoid flicker.
      setErrors([...syncErrors, ...extraErrors]);
      setValue(ATTACHMENTS_FIELD, next, { shouldValidate: false });
    },
    [constraints, setValue],
  );

  const addFiles = useCallback(
    async (incoming: FileList | File[]): Promise<void> => {
      const incomingArray = Array.from(incoming);
      const allowedMimeTypes = constraints?.allowedMimeTypes ?? DEFAULT_ALLOWED_MIME_TYPES;

      // Sniff magic bytes for each incoming file to detect disguised content.
      const contentErrors: AttachmentValidationError[] = [];
      const accepted: File[] = [];

      for (const file of incomingArray) {
        const bytes = await readHeaderBytes(file);
        const detected = sniffMimeType(bytes);

        if (detected === null || !allowedMimeTypes.includes(detected)) {
          contentErrors.push({
            filename: file.name,
            reason: 'type',
            message: `"${file.name}" has an unsupported type (${(detected ?? file.type) || 'unknown'}).`,
          });
        } else {
          accepted.push(file);
        }
      }

      // Use functional update so we read the actual current state, not a stale
      // closure capture — prevents races when two selections happen in quick succession.
      setFiles((prev) => {
        const next = [...prev, ...accepted];
        const syncErrors = validateAttachments(next, constraints);
        setErrors([...syncErrors, ...contentErrors]);
        setValue(ATTACHMENTS_FIELD, next, { shouldValidate: false });
        return next;
      });
    },
    [constraints, setValue],
  );

  const removeFile = useCallback(
    (index: number) => {
      commit(filesRef.current.filter((_, i) => i !== index));
    },
    [commit],
  );

  return { files, errors, addFiles, removeFile };
};
// #endregion

export { useAttachments };
